import { ComponentProps, createContext, useContext } from 'react';
import * as z from 'zod';
import { FormLabel, useFormField } from '@/components/ui-kit/form';

/** Field names (array indices as `*`) the form's schema requires; empty means no field is marked. */
const RequiredFieldsContext = createContext<ReadonlySet<string>>(new Set());

export const RequiredFieldsProvider = RequiredFieldsContext.Provider;

/** `SubCategoryExperiences.2.StartDate` -> `SubCategoryExperiences.*.StartDate`. */
const toPattern = (name: string) => name.replace(/\.\d+(?=\.|$)/g, '.*');

/**
 * The fields a schema requires: those it rejects in an empty form. Read from the schema itself so the
 * marks follow every rule, including the extended set a person must fill in on their own profile. Give
 * one empty row of each list to cover the fields inside list rows.
 */
export const requiredFieldsOf = (
  schema: z.ZodTypeAny,
  emptyValues: unknown
): ReadonlySet<string> => {
  const result = schema.safeParse(emptyValues);
  return new Set(
    result.success ? [] : result.error.issues.map((issue) => toPattern(issue.path.join('.')))
  );
};

/** A form label marked with `*` when its field is required; `required` overrides the schema. */
export const FieldLabel = ({
  required,
  children,
  ...props
}: ComponentProps<typeof FormLabel> & { required?: boolean }) => {
  const { name } = useFormField();
  const requiredFields = useContext(RequiredFieldsContext);
  const isRequired = required ?? (Boolean(name) && requiredFields.has(toPattern(name)));

  return (
    <FormLabel {...props}>
      {children}
      {isRequired && <span className="text-error"> *</span>}
    </FormLabel>
  );
};
